import { useState, useRef } from "react";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import useGet from "../read/useGet";
import usePut from "../put/usePut";

export const usePettyCashFundRequestDetail = ({
  requestId,
  onApprovalSuccess,
  onMarkAsPaidSuccess,
  onDeleteSuccess,
}) => {
  const [selectedTxnId, setSelectedTxnId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const txnLimit = 10;

  const initialTxnFilters = {
    request_id: requestId,
    page_number: 0,
    limit: txnLimit,
    search_query: "",
    approval_status: "",
    from_date: "",
    to_date: "",
  };

  // 1. Fetch Fund Request Lifecycle
  const {
    list: lifecycleQuery,
  } = useGet({
    url: "petty-cash/fund_request_lifecycle",
    initialData: { request_id: requestId },
    enabled: !!requestId,
  });

  // 2. Fetch Extended Single Fund Request Details
  const {
    list: singleRequestQuery,
  } = useGet({
    url: "petty-cash/view_fund_request",
    initialData: { request_id: requestId },
    enabled: !!requestId,
  });

  // 3. Fetch Transactions associated with this Fund Request
  const {
    list: transactionListing,
    paramsObject: txnFilters,
    setGetListParams: setTxnFilters,
  } = useGet({
    url: "petty-cash/fund_request_transaction_listing",
    initialData: initialTxnFilters,
    enabled: !!requestId,
  });

  const txnFiltersRef = useRef(txnFilters);
  txnFiltersRef.current = txnFilters;

  const debouncedSearch = useRef(
    debounce((value) => {
      const updated = {
        ...txnFiltersRef.current,
        request_id: requestId,
        search_query: value,
        page_number: 0,
      };
      setTxnFilters(updated);
    }, 300)
  ).current;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleFilterChange = (field, value) => {
    const updated = {
      ...txnFilters,
      request_id: requestId,
      [field]: value,
      page_number: 0,
    };
    setTxnFilters(updated);
  };

  const handleTxnPageClick = (val) => {
    const newPage = val.selected;
    const updated = {
      ...txnFilters,
      request_id: requestId,
      page_number: newPage,
    };
    setTxnFilters(updated);
  };

  // 4. Fetch Single Transaction Details (for detail modal / receipt view)
  const {
    list: activeTxnQuery,
    setGetListParams: setActiveTxnParams,
  } = useGet({
    url: "petty-cash/fetch_single_transaction",
    initialData: { transaction_id: selectedTxnId },
    enabled: !!selectedTxnId,
  });

  const handleViewTxnDetails = (txnId) => {
    setSelectedTxnId(txnId);
    setActiveTxnParams({ transaction_id: txnId });
  };

  // 5. Approve/Reject Fund Request Mutation (for Admin)
  const { handleAuth: approveRequestMutation } = usePut({
    url: "petty-cash/approve_fund_request",
    refreshUrl: [
      "petty-cash/fund_request_lifecycle",
      "petty-cash/view_fund_request",
      "petty-cash/fund_request_list",
      "petty-cash/fund_request_transaction_listing",
      "petty-cash/statistics",
    ],
    onSuccess: () => {
      toast.success("Fund request status updated successfully");
      onApprovalSuccess?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to update fund request status");
    },
  });
  const { mutate: approveRequest, isLoading: isApproving } = approveRequestMutation;

  // 6. Mark Fund Request as Paid Mutation (for Admin)
  const { handleAuth: markAsPaidMutation } = usePut({
    url: "petty-cash/mark_fund_request_as_paid",
    refreshUrl: [
      "petty-cash/fund_request_lifecycle",
      "petty-cash/view_fund_request",
      "petty-cash/fund_request_list",
      "petty-cash/fund_request_transaction_listing",
      "petty-cash/statistics",
      "petty-cash/transaction_listing",
    ],
    onSuccess: () => {
      toast.success("Fund request marked as paid successfully");
      onMarkAsPaidSuccess?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to mark fund request as paid");
    },
  });
  const { mutate: markAsPaid, isLoading: isMarkingAsPaid } = markAsPaidMutation;

  // 7. Delete Fund Request Mutation (for Employee)
  const { handleAuth: deleteRequestMutation } = usePut({
    url: "petty-cash/delete_fund_request",
    refreshUrl: ["petty-cash/fund_request_list"],
    onSuccess: () => {
      toast.success("Fund request deleted successfully");
      onDeleteSuccess?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to delete fund request");
    },
  });
  const { mutate: deleteRequest, isLoading: isDeleting } = deleteRequestMutation;

  return {
    lifecycleQuery,
    singleRequestQuery,
    transactionListing,
    activeTxnQuery,
    selectedTxnId,
    setSelectedTxnId,
    txnFilters,
    searchQuery,
    txnLimit,
    handleSearchChange,
    handleFilterChange,
    handleViewTxnDetails,
    handleTxnPageClick,
    approveRequest,
    isApproving,
    markAsPaid,
    isMarkingAsPaid,
    deleteRequest,
    isDeleting,
  };
};
